import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, ShieldCheck, Sparkles, Scale, Search, Star, Lock, MessageSquare, Zap, XCircle } from "lucide-react";
import { trustItems } from "./homeData";

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-3 py-2 bg-slate-100 rounded-2xl rounded-tl-sm w-fit">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" />
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "240ms" }} />
    </div>
  );
}

// Hero: headline A/B, CTAs, trust/urgency strips, free-ask search, chat preview and the trust bar.
export function HeroSection({ headlineVariant, chatPrice, consultPrice, ctaLabel, lawStats, query, setQuery, onAsk, onCta, onPreviewBook, navigate }) {
  return (
          <section className="relative overflow-hidden adv-navy">
            <div className="adv-blob bg-[#D4AF37]" style={{ width: 480, height: 480, top: -140, right: -120, opacity: 0.22 }} />
            <div className="adv-blob bg-[#1E40AF]" style={{ width: 420, height: 420, bottom: -180, left: -120, opacity: 0.35 }} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14 lg:pt-24 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center relative">
              <div className="lg:col-span-7 adv-fade-in text-white">
                <span className="adv-chip" data-testid="hero-chip">
                  <Sparkles size={14} /> India-first · AI + Lawyers
                </span>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" style={{ fontFamily: "Poppins" }} data-testid={`hero-headline-${headlineVariant || "default"}`}>
                  {headlineVariant === "legal_help_5min" ? (
                    <>
                      <span className="adv-gold-text">Legal Help</span>
                      <br /> <span className="text-white/95">in 5 Minutes.</span>
                    </>
                  ) : (
                    <>
                      Talk to a <span className="adv-gold-text">Lawyer</span>
                      <br /> <span className="text-white/95">Instantly.</span>
                    </>
                  )}
                </h1>
                <p className="mt-6 text-lg text-white/80 max-w-xl leading-relaxed">
                  Chat, call, or meet verified lawyers across India — <span className="font-bold text-[#D4AF37]">starting at ₹{chatPrice}</span>.
                </p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/lawyers" onClick={onCta}>
                    <Button data-testid="hero-consult-cta" className="rounded-full adv-btn-gold font-bold h-12 px-7 text-base shadow-lg shadow-black/20">
                      {ctaLabel} <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </Link>
                  <Link to="/chat">
                    <Button variant="outline" data-testid="hero-guidance-cta" className="rounded-full h-12 px-7 text-base border-white/30 bg-white/5 text-white hover:bg-white/10 hover:text-white font-semibold">
                      Get Quick Guidance (Free)
                    </Button>
                  </Link>
                </div>

                {/* Risk reversal line */}
                <p className="mt-4 text-sm text-white/85 font-medium flex items-center gap-1.5" data-testid="risk-reversal-line">
                  <MessageSquare size={14} className="text-[#D4AF37]" /> Start chat for free. Pay only if you continue.
                </p>

                {/* Privacy reassurance */}
                <p className="mt-2 text-sm text-white/80 font-medium flex items-center gap-1.5" data-testid="privacy-reassurance-line">
                  <Lock size={14} className="text-emerald-400" /> Your legal queries are <b>100% private &amp; encrypted</b>. Never shared.
                </p>

                {/* Social proof line */}
                <p className="mt-2 text-sm text-white/85 font-semibold flex items-center gap-1.5" data-testid="social-proof-line">
                  <Sparkles size={14} className="text-[#D4AF37]" /> Trusted by <b className="text-[#D4AF37]">2,400+ users</b> across India</p>

                {/* Trust + pricing strip */}
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/85" data-testid="hero-trust-strip">
                  <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-[#D4AF37]" fill="currentColor" /> <b>4.8/5</b> average rating</span>
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-400" /> <b>150+</b> verified lawyers</span>
                  <span className="inline-flex items-center gap-1.5"><MessageSquare size={14} className="text-[#D4AF37]" /> Chat starting <b>₹{chatPrice}</b></span>
                </div>

                {/* Urgency indicators */}
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/85" data-testid="urgency-strip">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="adv-pulse-dot" /> <b className="text-emerald-400">{lawStats.online || 12}</b> lawyers available right now
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Zap size={14} className="text-[#D4AF37]" /> Avg response time: <b>{lawStats.avg_response_mins || 2} mins</b>
                  </span>
                </div>

                {/* Search (secondary) */}
                <div className="mt-7 flex flex-col sm:flex-row gap-2 max-w-2xl bg-white rounded-2xl p-2 shadow-xl shadow-black/10">
                  <div className="flex-1 flex items-center gap-2 px-3">
                    <Search size={16} className="text-slate-400" />
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && onAsk()}
                      placeholder="Describe your legal issue (optional)…"
                      data-testid="hero-search-input"
                      className="border-0 shadow-none focus-visible:ring-0 text-[#0A2540] placeholder:text-slate-400"
                    />
                  </div>
                  <Button
                    onClick={onAsk}
                    data-testid="hero-ask-cta"
                    variant="outline"
                    className="rounded-xl font-bold h-10 px-5 border-slate-200 text-[#0A2540]"
                  >
                    Ask for free
                  </Button>
                </div>

                {/* Final trust boost near CTA */}
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/75 font-medium" data-testid="cta-trust-boost">
                  <span className="inline-flex items-center gap-1.5"><Lock size={12} className="text-[#D4AF37]" /> 100% private & confidential</span>
                  <span className="inline-flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> Verified lawyers only</span>
                  <span className="inline-flex items-center gap-1.5"><XCircle size={12} className="text-white/60" /> No obligation to continue</span>
                </div>
              </div>

              {/* Chat preview */}
              <div className="lg:col-span-5 adv-fade-in">
                <div className="relative rounded-3xl bg-white p-5 shadow-2xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-full adv-navy grid place-items-center"><Scale size={14} className="text-[#D4AF37]" /></div>
                      <div>
                        <p className="text-sm font-bold text-[#0A2540]">Adv. Priya Sharma</p>
                        <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1"><span className="adv-pulse-dot" /> Online · typing…</span>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Live chat</span>
                  </div>
                  <div className="space-y-2">
                    <div className="ml-auto max-w-[85%] bg-[#0A2540] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm">
                      My landlord is asking me to vacate without notice. What should I do?
                    </div>
                    <TypingDots />
                    <div className="max-w-[90%] bg-[#FBF4DF] text-[#0A2540] rounded-2xl rounded-tl-sm px-4 py-3 border border-[#F1E3B5] text-sm">
                      Don't worry — eviction requires proper written notice. Keep paying rent via bank/UPI to keep proof.
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button onClick={() => navigate("/chat")} data-testid="preview-continue-chat" className="rounded-xl adv-btn-primary text-sm font-bold py-2.5">
                      Start Free Chat →
                    </button>
                    <button onClick={onPreviewBook} data-testid="preview-book-15min" className="rounded-xl adv-btn-gold text-sm font-bold py-2.5">
                      Book 15-min Consult
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400 text-center">Free for first message · Chat from ₹{chatPrice} · Consult from ₹{consultPrice}</p>
                </div>
              </div>
            </div>

            {/* Trust bar heading + strip */}
            <div className="relative border-t border-white/10 bg-[#0A2540]/70 backdrop-blur-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
                <p className="text-center text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">
                  Trusted & Verified Across India
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="trust-bar">
                  {trustItems.map((t) => (
                    <div key={t.label} className="flex items-center justify-center md:justify-start gap-2 text-xs sm:text-sm text-white/85 font-semibold">
                      <t.icon size={16} className="text-[#D4AF37] shrink-0" />
                      {t.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
  );
}
